import { useMemo, useState } from 'react';

// types
import { OrganiseEntry } from '@karehero/models';
import DialogWithConfirmActions from '@/pwa/atoms/DialogWithConfirmActions/DialogWithConfirmActions';

import OrganiserEntryForm, {
  EntryDialogCalendar,
  EntryState,
  isFormValid,
} from '../OrganiserEntryForm/OrganiserEntryForm';

export interface EditEntryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Available calendars (care circles) — shown as a picker in create mode. */
  calendars: EntryDialogCalendar[];
  onSave: (entry: EditEntryState) => void;
  onDelete: (calendarId: string, entryId: string) => void;
  /** Cancel this occurrence (repeating tasks in edit mode). */
  onCancelOccurrence: (
    calendarId: string,
    recurringEventId?: string | null,
    occurrenceDate?: string | null,
  ) => void;
  /** Delete the entire repeating task (repeating entries in edit mode). */
  onDeleteSeries: (
    calendarId: string,
    recurringEventId?: string | null,
  ) => void;
  isSaving?: boolean;
  entry: OrganiseEntry;
}

export interface EditEntryState extends EntryState {
  entryId: string;
}

const initialEditEntryState = (entry: OrganiseEntry) => {
  return {
    calendarId: entry.calendarId,
    entryId: entry.id,
    title: entry.title,
    assigneeAccountId: entry.assigneeAccountId ?? null,
    startTime: entry.startTime ?? null,
    endTime: entry.endTime ?? null,
    isRecurring: false,
    recurrenceFrequency: 'weekly',
    recurrenceInterval: 1,
    isRepeatingTaskEntry: !!entry.recurringEventId,
    isUpdatingRepeatingTask: false,
    recurringEventId: entry.recurringEventId ?? null,
    occurrenceDate: entry.occurrenceDate ?? null,
  } as EditEntryState;
};

/**
 * EntryDialog is a MUI Dialog for editing an organiser entry.
 */
export const EditEntryDialog = ({
  isOpen,
  onClose,
  entry,
  calendars,
  onSave,
  onDelete,
  onCancelOccurrence,
  onDeleteSeries,
  isSaving = false,
}: EditEntryDialogProps) => {
  const [draftEntry, setDraftEntry] = useState<EditEntryState>(
    initialEditEntryState(entry),
  );

  const isRecurringEntry = useMemo(
    () => draftEntry.isRepeatingTaskEntry,
    [draftEntry],
  );
  const isValid = useMemo(() => isFormValid(draftEntry), [draftEntry]);

  const editActions = useMemo(() => {
    return [
      {
        variant: 'contained',
        size: 'large',
        label: isSaving ? 'Saving...' : 'Save',
        onAction: () => onSave(draftEntry),
        isDisabled: isSaving || !isValid,
      },
      isRecurringEntry &&
        draftEntry.recurringEventId && {
          variant: 'outlined',
          size: 'large',
          color: 'primary',
          label: 'Skip this action',
          onAction: () =>
            onCancelOccurrence(
              draftEntry.calendarId,
              draftEntry.recurringEventId,
              draftEntry.occurrenceDate,
            ),
        },
      !isRecurringEntry && {
        variant: 'text',
        color: 'error',
        label: 'Delete',
        onAction: () => onDelete(draftEntry.calendarId, draftEntry.entryId),
        isConfirmable: true,
        warningText:
          'This will permanently delete this action. This cannot be undone.',
        confirmText: 'Yes, delete',
      },
      isRecurringEntry && {
        variant: 'text',
        color: 'error',
        label: 'Delete all repeated actions',
        onAction: () =>
          onDeleteSeries(draftEntry.calendarId, draftEntry.recurringEventId),
        isConfirmable: true,
        warningText:
          'This will permanently delete all repeated actions. This cannot be undone.',
        confirmText: 'Yes, delete all',
      },
    ].filter((x) => !!x);
  }, [
    isValid,
    draftEntry,
    isSaving,
    onSave,
    onCancelOccurrence,
    onDelete,
    onDeleteSeries,
    isRecurringEntry,
  ]);

  return (
    <DialogWithConfirmActions
      title='Edit action'
      isOpen={isOpen}
      onClose={onClose}
      actions={editActions}
    >
      <OrganiserEntryForm
        draftEntry={draftEntry}
        setDraftEntry={setDraftEntry}
        mode='edit'
        calendars={calendars}
      />
    </DialogWithConfirmActions>
  );
};

EditEntryDialog.displayName = 'EditEntryDialog';
export default EditEntryDialog;
